import { useEffect, useState } from "react";
import { EvaluatorLayout } from "./EvaluatorLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import { ClipboardCheck, School, TrendingUp, Award } from "lucide-react";
import { supabase } from "@/project/karuna/integrations/supabase/client"; // @ts-ignore - supabase is not a valid module

const EvaluatorDashboard = () => {
  const [stats, setStats] = useState({
    pendingSubmissions: 0,
    totalSchools: 0,
    avgScore: 0,
    evaluatedToday: 0,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const { data: submissions } = await supabase
      .from("event_submissions")
      .select("*");

    const { data: schools } = await supabase.from("schools").select("id");

    const pending =
      submissions?.filter(s => s.status === "pending").length ?? 0;
    const scoredSubmissions = submissions?.filter(s => s.score) ?? [];
    const avgScore =
      scoredSubmissions.length > 0
        ? scoredSubmissions.reduce((acc, s) => acc + (s.score ?? 0), 0) /
          scoredSubmissions.length
        : 0;

    setStats({
      pendingSubmissions: pending,
      totalSchools: schools?.length ?? 0,
      avgScore: Math.round(avgScore),
      evaluatedToday: 0,
    });
  };

  const statCards = [
    {
      title: "Pending Reviews",
      value: stats.pendingSubmissions.toString(),
      icon: ClipboardCheck,
      color: "text-orange-600",
      bgColor: "bg-orange-500/10",
    },
    {
      title: "Total Schools",
      value: stats.totalSchools.toString(),
      icon: School,
      color: "text-blue-600",
      bgColor: "bg-blue-500/10",
    },
    {
      title: "Average Score",
      value: `${stats.avgScore}%`,
      icon: TrendingUp,
      color: "text-green-600",
      bgColor: "bg-green-500/10",
    },
    {
      title: "Evaluated Today",
      value: stats.evaluatedToday.toString(),
      icon: Award,
      color: "text-purple-600",
      bgColor: "bg-purple-500/10",
    },
  ];

  return (
    <EvaluatorLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Evaluator Dashboard</h1>
            <p className="mt-1 text-muted-foreground">
              Review and evaluate school activity submissions
            </p>
          </div>
          <Badge variant="outline" className="px-3 py-1 text-sm">
            Academic Year 2024-25
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map(stat => (
            <Card
              key={stat.title}
              className="overflow-hidden transition-shadow hover:shadow-lg"
            >
              <CardContent className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div
                    className={`h-12 w-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}
                  >
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
                <div className="mb-1 text-3xl font-bold">{stat.value}</div>
                <div className="text-sm text-muted-foreground">
                  {stat.title}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Card className="cursor-pointer transition-colors hover:bg-muted/50">
                <CardContent className="p-4 text-center">
                  <ClipboardCheck className="mx-auto mb-2 h-8 w-8 text-primary" />
                  <p className="font-medium">Review Pending</p>
                  <p className="text-sm text-muted-foreground">
                    {stats.pendingSubmissions} submissions
                  </p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer transition-colors hover:bg-muted/50">
                <CardContent className="p-4 text-center">
                  <Award className="mx-auto mb-2 h-8 w-8 text-primary" />
                  <p className="font-medium">View Leaderboard</p>
                  <p className="text-sm text-muted-foreground">
                    Top performers
                  </p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer transition-colors hover:bg-muted/50">
                <CardContent className="p-4 text-center">
                  <School className="mx-auto mb-2 h-8 w-8 text-primary" />
                  <p className="font-medium">School Reports</p>
                  <p className="text-sm text-muted-foreground">
                    {stats.totalSchools} schools
                  </p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      </div>
    </EvaluatorLayout>
  );
};

export default EvaluatorDashboard;
